import type { FormEvent, MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, PrimaryButton, TextInput } from '@luminalearning/components';
import type { EnvironmentKey } from 'src/Components/Pages/Developer';

type UrlValues = {
    exitRedirectUrl: string;
    completeRedirectUrl: string;
    completeNotificationHookUrl: string;
    pdfReadyNotificationHookUrl: string;
    pdfDownloadHookUrl: string;
};

type SaveStatus = {
    tone: 'Success' | 'Error';
    message: string;
    isShow: boolean;
};

type WebhookField = {
    key: keyof UrlValues;
    urlType: string;
    labelMessage: { id: string; defaultMessage: string };
    descriptionMessage: { id: string; defaultMessage: string };
    placeholder: string;
};

type WebhookSectionProps = {
    fields: WebhookField[];
    activeEnvironmentTab: EnvironmentKey;
    activeValues: UrlValues;
    statusByKey: Record<string, SaveStatus | undefined>;
    savingKeys: Record<string, boolean>;
    onSubmit: (
        event: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>,
        environment: EnvironmentKey,
        key: keyof UrlValues,
    ) => void;
    onUpdateValue: (environment: EnvironmentKey, key: keyof UrlValues, value: string) => void;
};

const WebhookSection = ({
    fields,
    activeEnvironmentTab,
    activeValues,
    statusByKey,
    savingKeys,
    onSubmit,
    onUpdateValue,
}: WebhookSectionProps) => {
    const intl = useIntl();

    return (
        <>
            <h2 className="mt-8 text-2xl font-bold">
                <FormattedMessage id="LP_DEVELOPER_CONFIGURATION_SECTION" defaultMessage="Configuration" />
            </h2>
            <div className='mt-2 text-paragraph-reg max-w-3xl'>
                <FormattedMessage
                    id="LP_DEVELOPER_ENVIRONMENT_DESCRIPTION"
                    defaultMessage="Configure webhook and redirect URLs."
                />
            </div>
            <div className='mt-2 w-full xl:pr-4 2xl:pr-20 3xl:pr-2'>
                {fields.map((field) => {
                    const statusKey = `${activeEnvironmentTab}-${field.key}`;
                    const status = statusByKey[statusKey];
                    const isSaving = !!savingKeys[statusKey];

                    return (
                        <form
                            key={statusKey}
                            className='mt-6 border-1.5 border-card-normal rounded-lg p-6 bg-white shadow-card'
                            onSubmit={(event) => onSubmit(event, activeEnvironmentTab, field.key)}
                        >
                            <div className='flex flex-col gap-y-3'>
                                <span className='text-headline-reg font-bold'>
                                    <FormattedMessage {...field.labelMessage} />
                                </span>
                                <span className='text-paragraph-reg'>
                                    <FormattedMessage {...field.descriptionMessage} />
                                </span>
                                {status && status.isShow && (
                                    <Alert type={status.tone} message={status.message} />
                                )}
                                <TextInput
                                    value={activeValues[field.key]}
                                    placeholder={field.placeholder}
                                    setValue={(value) => onUpdateValue(activeEnvironmentTab, field.key, value)}
                                    className='w-full'
                                    isDisabled={isSaving}
                                />
                                <div className='flex justify-end'>
                                    <PrimaryButton
                                        type='submit'
                                        isDisabled={isSaving}
                                        isLoading={isSaving}
                                        text={intl.formatMessage({
                                            id: 'LP_SAVE',
                                            defaultMessage: 'Save',
                                        })}
                                        onClick={(event) => onSubmit(
                                            event, activeEnvironmentTab, field.key
                                        )}
                                    />
                                </div>
                            </div>
                        </form>
                    );
                })}
            </div>
        </>
    );
};

export default WebhookSection;
